// @ts-nocheck
import {
    add,
    eachDayOfInterval,
    format,
    getUnixTime,
    startOfWeek,
    lastDayOfWeek,
    startOfMonth,
    lastDayOfMonth,
    endOfWeek,
    isAfter,
    isSameDay,
    isWithinInterval,
    isValid,
    differenceInCalendarDays,
    isMonday,
    isSunday,
    isBefore,
    addDays,
    fromUnixTime,
} from 'date-fns';
import { CalendarIntervalDay } from 'platform-components';
import qs from 'query-string';

import { MarkedDay } from 'common.components/week-picker/week-picker';

import { COURSE_CALENDAR_MIN_DATE, MARKED_DAY_COLOR, MILLISECONDS_IN_MINUTE, MINUTES_IN_HOUR } from './constants';
import { CalendarCardType, CalendarCourse, MarkedMonthDays } from './typings';

const getDate = (date: string | Date) => (typeof date === 'string' ? new Date(date) : date);

export const getCardId = (id: string | number, prefix: CalendarCardType) => `${prefix}:${id}`;

export const getMonthIntervalId = (start: string, end: string) => `${start}:${end}`;

export const getInterval = (start: string | Date, end: string | Date): CalendarIntervalDay[] => {
    start = getDate(start);
    end = getDate(end);

    return eachDayOfInterval({ start, end }).map((day) => {
        const timezoneOffset = day.getTimezoneOffset();
        const utc0midnight = new Date(day.getTime() - timezoneOffset * MILLISECONDS_IN_MINUTE);

        return {
            date: format(day, 'yyyy-MM-dd'),
            timestamp: getUnixTime(utc0midnight),
            timezone: (timezoneOffset * -1) / MINUTES_IN_HOUR,
        };
    });
};

export const getPeriodSearchString = (
    { start, end }: { start: string | Date; end: string | Date },
    searchParams: Record<string, string>,
    duration?: Duration
): string => {
    start = getDate(start);
    end = getDate(end);

    if (duration) {
        start = add(start, duration);
        end = add(end, duration);
    }

    return qs.stringify({
        ...searchParams,
        start: format(start, 'yyyy-MM-dd'),
        end: format(end, 'yyyy-MM-dd'),
    });
};

export const getCurrentWeek = (): { start: string; end: string } => {
    const date = new Date();

    return {
        start: format(startOfWeek(date, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        end: format(endOfWeek(date, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
    };
};

export const getMonthIntervalFromWeek = (date: string | Date): CalendarIntervalDay[] => {
    date = getDate(date);

    return getInterval(
        startOfWeek(startOfMonth(date), { weekStartsOn: 1 }),
        lastDayOfWeek(lastDayOfMonth(date), { weekStartsOn: 1 })
    );
};

export const formatMarkedMonthDaysForWeekPicker = (marked: MarkedMonthDays) => {
    // TODO избавиться от new Date
    return Object.keys(marked).reduce((result: MarkedDay[], id) => {
        Object.keys(marked[id]).forEach((day) => {
            result.push({
                day: new Date(day),
                color: MARKED_DAY_COLOR,
            });
        });

        return result;
    }, []);
};

export const getMetrikaWeek = (start: string | Date, end: string | Date) => {
    start = getDate(start);
    end = getDate(end);

    return `${format(start, 'dd.MM.yyyy')}-${format(end, 'dd.MM.yyyy')}`;
};

export const splitIntervalByCourses = (interval: CalendarIntervalDay[], courses: CalendarCourse[]) => {
    const intervals: { courseId: number; interval: CalendarIntervalDay[] }[] = [];

    courses.forEach((course) => {
        const start = course.start && new Date(course.start);
        const end = course.end && new Date(course.end);
        const courseInterval: CalendarIntervalDay[] = [];

        interval.forEach((day) => {
            const date = new Date(day.date);
            let isInInterval = false;

            if (end && start) {
                isInInterval = isWithinInterval(date, { start, end });
            } else if (start && !end) {
                isInInterval = isSameDay(date, start) || isAfter(date, start);
            } else {
                isInInterval = true;
            }

            if (isInInterval) {
                courseInterval.push(day);
            }
        });

        if (courseInterval.length) {
            intervals.push({ courseId: course.courseId, interval: courseInterval });
        }
    });

    return intervals;
};

export const isValidPeriod = (start: string | Date, end: string | Date) => {
    start = getDate(start);
    end = getDate(end);

    return (
        isBefore(COURSE_CALENDAR_MIN_DATE, end) &&
        isValid(start) &&
        isValid(end) &&
        differenceInCalendarDays(end, start) === 6 &&
        isMonday(start) &&
        isSunday(end)
    );
};

export const addDaysTimestamp = (timestamp: number, amount: number) =>
    getUnixTime(addDays(fromUnixTime(timestamp), amount));
